import React, { useState } from 'react';
import { requestDonorOTP, verifyDonorOTP } from '../api';

const MyDonations = () => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [step, setStep] = useState('phone'); // 'phone', 'otp', or 'donations'
  const [donations, setDonations] = useState([]);

  const handlePhoneSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    if (!phoneNumber || phoneNumber.length < 10) {
      setError('Please enter a valid phone number');
      return;
    }
    
    setIsLoading(true);
    
    try {
      const result = await requestDonorOTP(phoneNumber);
      if (result.success) {
        setSuccess('OTP sent to your WhatsApp number');
        setStep('otp');
      } else {
        setError(result.message || 'Failed to send OTP');
      }
    } catch (err) {
      console.error(err);
      setError('No donations found for this phone number or server error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    if (!otp || otp.length !== 6) {
      setError('Please enter a valid 6-digit OTP');
      return;
    }
    
    setIsLoading(true);
    
    try {
      const result = await verifyDonorOTP(phoneNumber, otp);
      if (result.success) {
        setDonations(result.donations);
        setStep('donations');
      } else {
        setError(result.message || 'Invalid OTP');
      }
    } catch (err) {
      console.error(err);
      setError('Failed to verify OTP');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  return (
    <div className="container mx-auto py-12 px-4">
      <div className="max-w-3xl mx-auto bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="p-8">
          <h1 className="text-3xl font-bold text-center mb-8 text-primary">My Donations</h1>
          
          {error && (
            <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6" role="alert">
              <p>{error}</p>
            </div>
          )}
          
          {success && (
            <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-6" role="alert">
              <p>{success}</p>
            </div>
          )}
          
          {step === 'phone' && (
            <form onSubmit={handlePhoneSubmit} className="space-y-6">
              <div className="space-y-2">
                <label htmlFor="phoneNumber" className="block text-gray-700 font-medium">
                  WhatsApp Phone Number
                </label>
                <div className="flex">
                  <span className="inline-flex items-center px-3 py-2 rounded-l-md border border-r-0 border-gray-400 bg-gray-50 text-gray-500">
                    +91
                  </span>
                  <input
                    type="tel"
                    id="phoneNumber"
                    name="phoneNumber"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    className="mt-1 block w-full rounded-r-md border border-gray-400 shadow-sm focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50 py-2 px-3"
                    required
                    placeholder="Enter your WhatsApp number"
                    maxLength="10"
                  />
                </div>
                <p className="text-sm text-gray-500">
                  Enter the phone number used during donation
                </p>
              </div>
              
              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-primary text-white py-2 px-4 rounded hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-50 transition duration-150 ease-in-out flex justify-center items-center"
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Sending OTP...
                  </>
                ) : (
                  "Send OTP"
                )}
              </button>
            </form>
          )}
          
          {step === 'otp' && (
            <form onSubmit={handleOtpSubmit} className="space-y-6">
              <div className="space-y-2">
                <label htmlFor="otp" className="block text-gray-700 font-medium">
                  Enter OTP
                </label>
                <input
                  type="text"
                  id="otp"
                  name="otp"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  className="mt-1 block w-full rounded-md border border-gray-400 shadow-sm focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50 py-2 px-3"
                  required
                  placeholder="Enter 6-digit OTP received on WhatsApp"
                  maxLength="6"
                />
                <p className="text-sm text-gray-500">
                  OTP has been sent to your WhatsApp number
                </p>
              </div>
              
              <div className="flex space-x-4">
                <button
                  type="button"
                  onClick={() => setStep('phone')}
                  className="w-1/2 bg-gray-300 text-gray-700 py-2 px-4 rounded hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50 transition duration-150 ease-in-out"
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-1/2 bg-primary text-white py-2 px-4 rounded hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-50 transition duration-150 ease-in-out flex justify-center items-center"
                >
                  {isLoading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Verifying...
                    </>
                  ) : (
                    "Verify OTP"
                  )}
                </button>
              </div>
            </form>
          )}
          
          {step === 'donations' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold">Your Donation History</h2>
                <button
                  onClick={() => {
                    setStep('phone');
                    setPhoneNumber('');
                    setOtp('');
                  }}
                  className="text-primary hover:underline"
                >
                  Check Another Number
                </button>
              </div>
              
              {donations.length === 0 ? (
                <div className="text-center py-10">
                  <p className="text-gray-500">No donation records found</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {donations.map((donation, index) => (
                    <div key={index} className="py-4">
                      <div className="flex justify-between items-center mb-2">
                        <div className="font-semibold">
                          Receipt #{donation.receiptNumber}
                        </div>
                        <div className="text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded">
                          {donation.donationType === 'online' ? 'Online' : 
                           donation.donationType === 'cash' ? 'Cash' : 
                           donation.donationType === 'cheque' ? 'Cheque' : 
                           donation.donationType === 'upi' ? 'UPI' : 'Other'}
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex items-center">
                          <span className="text-primary mr-2">₹</span>
                          <span>{donation.amount}</span>
                        </div>
                        
                        <div className="flex items-center">
                          <span className="text-primary mr-2">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          </span>
                          <span>{formatDate(donation.date)}</span>
                        </div>
                      </div>
                      
                      {donation.note && (
                        <div className="mt-2 text-gray-600 italic">
                          "{donation.note}"
                        </div>
                      )}
                      
                      <div className="mt-3">
                        <a
                          href={`/receipt-share?id=${donation._id}&type=${donation.donationType === 'online' ? 'online' : 'cash'}`}
                          className="text-primary hover:underline flex items-center"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          View Receipt
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MyDonations;
